import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CasingPurchaseFiltersResponse, Pagination } from '../../models/response.model';
import { map } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CasingPurchase } from '../../models/casing-purchase.model';
import { Router } from '@angular/router';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { CustomerAssignService } from '../../../../shared/services/customer-assign.service';

@Component({
  selector: 'app-casing-purchase',
  templateUrl: './casing-purchase.component.html',
  styleUrl: './casing-purchase.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingPurchaseComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingPurchase>;
  codesList: string[] = [];
  descriptionList: string[] = [];
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public customerAssignService = inject(CustomerAssignService);
  public cdr = inject(ChangeDetectorRef);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingPurchases: CasingPurchase[] = [];
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  currentCasingPurchase: CasingPurchase;
  productSaveDisabled = true;
  casingPurchaseBreadcrumbs = [
    { link: '/home/dashboard', icon: 'home' },
    { label: this.translationService.translate('productDataText') },
    { link: '/home/productData/casingPurchase', label: this.translationService.translate('casingPurchaseText') }
  ];
  additionalColumns = [
    { property: 'casingPurchaseCode', headerKey: this.translationService.translate('codeText') },
    { property: 'casingPurchaseDesc', headerKey: this.translationService.translate('descriptionText') }
  ];

  tableConfig: TableConfig<CasingPurchase> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {},
    onRowClick: (elm: CasingPurchase) => {
      this.router.navigateByUrl(`/home/productData/casingPurchase/casingPurchaseSetup/${elm.casingPurchasePk}`, {
        state: { code: elm.purchaseCode, description: elm.description }
      });
    }
  };

  fetcher: PaginatorFetcher<CasingPurchase> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getCasingPurchases({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.casingPurchases = response.data.casingPurchases || [];
        this.pagination = response.data.pagination;
        return { items: this.casingPurchases, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      code: [[]],
      description: [[]]
    });
    this.filterFormBackend = this.fb.group({
      code: [[]],
      description: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getCasingPurchaseFilters().subscribe((response: CasingPurchaseFiltersResponse) => {
      this.codesList = response.data.purchaseCodes || [];
      this.descriptionList = response.data.purchaseDescriptions || [];
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        purchaseCode: { cell: (row) => row.purchaseCode, header: this.translationService.translate('codeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') }
      };
      this.cdr.markForCheck();
    });
  }

  assignToCustomer(item: CasingPurchase) {
    this.currentCasingPurchase = item;
    const navigationData = {
      breadcrumbLinks: this.casingPurchaseBreadcrumbs,
      currentDataObject: this.currentCasingPurchase,
      additionalColumns: this.additionalColumns,
      searchUri: 'searchCasingPurchaseAssignToCustomer',
      assignUri: 'assignCustomerCasingPurchase',
      service: 'backOfficeUrl'
    };

    this.customerAssignService.setNavigationData(navigationData);
    this.router.navigate(['/home/productData/customer-assign', this.currentCasingPurchase.casingPurchasePk]);
  }

  copyCasingPurchase(item: CasingPurchase) {
    this.router.navigateByUrl(`/home/productData/casingPurchase/createCasingPurchase/${item.casingPurchasePk}`);
  }

  clearFilters(): void {
    this.filterForm.reset();
  }

  createCasingPurchase(): void {
    this.router.navigateByUrl(`/home/productData/casingPurchase/createCasingPurchase`);
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      code: this.filterForm.get('code')?.value || '',
      description: this.filterForm.get('description')?.value || ''
    });
  }

  protected readonly localStorage = localStorage;
  protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
}
